import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  Badge,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Share as ShareIcon,
  Security as SecurityIcon,
  Block as BlockIcon,
  Visibility as ViewIcon,
  Delete as RevokeIcon,
  Warning as WarningIcon,
  CheckCircle as VerifiedIcon,
  Schedule as TimerIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  Shield as ShieldIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface TripShare {
  id: string;
  shareId: string;
  bookingId: string;
  sharedBy: {
    id: string;
    name: string;
    role: 'renter' | 'driver';
  };
  recipients: Array<{
    phone: string;
    verificationStatus: 'pending' | 'verified' | 'failed';
    lastAccess?: string;
    accessCount: number;
  }>;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'revoked';
  securityMetrics: {
    totalViews: number;
    uniqueViewers: number;
    suspiciousActivity: number;
    revokeAttempts: number;
  };
  tripDetails: {
    vehicle: string;
    route: string;
    driver?: string;
    currentStatus: string;
  };
}

interface SecuritySettings {
  maxSharesPerDay: number;
  shareExpiryHours: number;
  maxVerificationAttempts: number;
  requirePhoneVerification: boolean;
  enableBrowserFingerprinting: boolean;
  rateLimitViews: number;
}

const TripSharingManagementPage: React.FC = () => {
  const theme = useTheme();
  const [tripShares, setTripShares] = useState<TripShare[]>([]);
  const [selectedShare, setSelectedShare] = useState<TripShare | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired' | 'revoked'>('all');
  
  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    maxSharesPerDay: 10,
    shareExpiryHours: 12,
    maxVerificationAttempts: 3,
    requirePhoneVerification: true,
    enableBrowserFingerprinting: true,
    rateLimitViews: 20,
  });

  const [stats, setStats] = useState({
    activeShares: 45,
    totalVerifications: 234,
    suspiciousActivities: 3,
    revokedShares: 8,
    averageVerificationTime: '2.3m',
    securityScore: 94,
  });

  useEffect(() => {
    fetchTripShares();
    const interval = setInterval(fetchTripShares, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchTripShares = async () => {
    // Mock data - replace with actual API call
    const mockShares: TripShare[] = [
      {
        id: '1',
        shareId: 'sh_abc123',
        bookingId: 'bk_789',
        sharedBy: { id: 'u1', name: 'Sarah Johnson', role: 'renter' },
        recipients: [
          { phone: '+234***4567', verificationStatus: 'verified', lastAccess: '2024-01-20T14:30:00Z', accessCount: 5 },
          { phone: '+234***7890', verificationStatus: 'pending', accessCount: 0 },
        ],
        createdAt: '2024-01-20T14:00:00Z',
        expiresAt: '2024-01-21T02:00:00Z',
        status: 'active',
        securityMetrics: { totalViews: 12, uniqueViewers: 2, suspiciousActivity: 0, revokeAttempts: 0 },
        tripDetails: {
          vehicle: 'Toyota Camry 2023',
          route: 'VI → Lekki → VI',
          driver: 'James Smith',
          currentStatus: 'en-route',
        },
      },
      {
        id: '2',
        shareId: 'sh_def456',
        bookingId: 'bk_456',
        sharedBy: { id: 'd1', name: 'Michael Brown', role: 'driver' },
        recipients: [
          { phone: '+234***1234', verificationStatus: 'verified', lastAccess: '2024-01-20T13:45:00Z', accessCount: 3 },
        ],
        createdAt: '2024-01-20T13:30:00Z',
        expiresAt: '2024-01-21T01:30:00Z',
        status: 'active',
        securityMetrics: { totalViews: 8, uniqueViewers: 1, suspiciousActivity: 1, revokeAttempts: 0 },
        tripDetails: {
          vehicle: 'BMW 5 Series',
          route: 'Airport → Ikoyi',
          driver: 'Michael Brown',
          currentStatus: 'pickup',
        },
      },
      {
        id: '3',
        shareId: 'sh_ghi789',
        bookingId: 'bk_123',
        sharedBy: { id: 'u2', name: 'John Doe', role: 'renter' },
        recipients: [
          { phone: '+234***5678', verificationStatus: 'failed', accessCount: 3 },
          { phone: '+234***9012', verificationStatus: 'verified', lastAccess: '2024-01-20T12:00:00Z', accessCount: 2 },
        ],
        createdAt: '2024-01-20T10:00:00Z',
        expiresAt: '2024-01-20T22:00:00Z',
        status: 'revoked',
        securityMetrics: { totalViews: 15, uniqueViewers: 2, suspiciousActivity: 2, revokeAttempts: 1 },
        tripDetails: {
          vehicle: 'Mercedes C300',
          route: 'Surulere → Ikeja',
          currentStatus: 'completed',
        },
      },
    ];
    
    setTripShares(mockShares);
  };

  const handleRevokeShare = async (shareId: string) => {
    // API call to revoke share
    setTripShares(prev => 
      prev.map(share => 
        share.id === shareId 
          ? { ...share, status: 'revoked' as const }
          : share
      )
    );
    setRevokeDialogOpen(false);
    setSelectedShare(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'warning';
      case 'revoked': return 'error';
      default: return 'default';
    }
  };

  const getVerificationIcon = (status: string) => {
    switch (status) {
      case 'verified': return <VerifiedIcon color="success" fontSize="small" />;
      case 'pending': return <TimerIcon color="warning" fontSize="small" />;
      case 'failed': return <WarningIcon color="error" fontSize="small" />;
      default: return null;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return format(date, 'MMM d, HH:mm');
  };

  const filteredShares = tripShares.filter(share => {
    if (statusFilter !== 'all' && share.status !== statusFilter) return false;
    if (searchQuery && !share.bookingId.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !share.sharedBy.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Trip Sharing Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor and manage secure trip sharing activities across the platform
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ShareIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.activeShares}</Typography>
              <Typography variant="caption" color="text.secondary">Active Shares</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <VerifiedIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.totalVerifications}</Typography>
              <Typography variant="caption" color="text.secondary">Verifications</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <WarningIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.suspiciousActivities}</Typography>
              <Typography variant="caption" color="text.secondary">Suspicious</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <BlockIcon color="error" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.revokedShares}</Typography>
              <Typography variant="caption" color="text.secondary">Revoked</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TimerIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.averageVerificationTime}</Typography>
              <Typography variant="caption" color="text.secondary">Avg Verification</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ShieldIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.securityScore}%</Typography>
              <Typography variant="caption" color="text.secondary">Security Score</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by booking ID or user name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ minWidth: 300 }}
          />
          
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            sx={{ minWidth: 120 }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </TextField>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTripShares}
          >
            Refresh
          </Button>

          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </Button>
        </Box>
      </Paper>

      {/* Trip Shares Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Share Details</TableCell>
              <TableCell>Shared By</TableCell>
              <TableCell>Recipients</TableCell>
              <TableCell>Trip Info</TableCell>
              <TableCell>Security</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredShares.map((share) => (
              <TableRow key={share.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {share.shareId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Booking: {share.bookingId}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Created: {formatTimeAgo(share.createdAt)}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">{share.sharedBy.name}</Typography>
                      <Chip label={share.sharedBy.role} size="small" />
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <List dense sx={{ py: 0 }}>
                    {share.recipients.map((recipient, idx) => (
                      <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          {getVerificationIcon(recipient.verificationStatus)}
                        </ListItemIcon>
                        <ListItemText
                          primary={recipient.phone}
                          secondary={`${recipient.accessCount} views`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TableCell>

                <TableCell>
                  <Box>
                    <Typography variant="body2">{share.tripDetails.vehicle}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {share.tripDetails.route}
                    </Typography>
                    <br />
                    <Chip 
                      label={share.tripDetails.currentStatus} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                </TableCell>

                <TableCell>
                  <Box>
                    <Typography variant="caption" display="block">
                      Views: {share.securityMetrics.totalViews} ({share.securityMetrics.uniqueViewers} unique)
                    </Typography>
                    {share.securityMetrics.suspiciousActivity > 0 && (
                      <Chip 
                        label={`${share.securityMetrics.suspiciousActivity} suspicious`} 
                        size="small" 
                        color="warning" 
                        icon={<WarningIcon />}
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell>
                  <Chip 
                    label={share.status} 
                    color={getStatusColor(share.status) as any}
                    size="small"
                  />
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setSelectedShare(share);
                          setDetailsOpen(true);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {share.status === 'active' && (
                      <Tooltip title="Revoke Share">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => {
                            setSelectedShare(share);
                            setRevokeDialogOpen(true);
                          }}
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Trip Share Details - {selectedShare?.shareId}
        </DialogTitle>
        <DialogContent>
          {selectedShare && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Share Information</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Share ID" secondary={selectedShare.shareId} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Booking ID" secondary={selectedShare.bookingId} />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Created" 
                      secondary={format(new Date(selectedShare.createdAt), 'PPpp')} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Expires" 
                      secondary={format(new Date(selectedShare.expiresAt), 'PPpp')} 
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Security Metrics</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Total Views" secondary={selectedShare.securityMetrics.totalViews} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Unique Viewers" secondary={selectedShare.securityMetrics.uniqueViewers} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Suspicious Activity" secondary={selectedShare.securityMetrics.suspiciousActivity} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Revoke Attempts" secondary={selectedShare.securityMetrics.revokeAttempts} />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Recipients</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Phone</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Access Count</TableCell>
                        <TableCell>Last Access</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedShare.recipients.map((recipient, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{recipient.phone}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getVerificationIcon(recipient.verificationStatus)}
                              {recipient.verificationStatus}
                            </Box>
                          </TableCell>
                          <TableCell>{recipient.accessCount}</TableCell>
                          <TableCell>
                            {recipient.lastAccess 
                              ? format(new Date(recipient.lastAccess), 'MMM d, HH:mm')
                              : 'Never'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          {selectedShare?.status === 'active' && (
            <Button 
              color="error" 
              onClick={() => {
                setDetailsOpen(false);
                setRevokeDialogOpen(true);
              }}
            >
              Revoke Share
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
        <DialogTitle color="error">Revoke Trip Share</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will immediately revoke access to the shared trip for all recipients.
          </Alert>
          <Typography>
            Are you sure you want to revoke the trip share "{selectedShare?.shareId}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
          <Button 
            color="error" 
            variant="contained"
            onClick={() => selectedShare && handleRevokeShare(selectedShare.id)}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trip Sharing Security Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Shares Per Day"
                type="number"
                value={securitySettings.maxSharesPerDay}
                onChange={(e) => setSecuritySettings(prev => ({
                  ...prev,
                  maxSharesPerDay: parseInt(e.target.value)
                }))}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Share Expiry (Hours)"
                type="number"
                value={securitySettings.shareExpiryHours}
                onChange={(e) => setSecuritySettings(prev => ({
                  ...prev,
                  shareExpiryHours: parseInt(e.target.value)
                }))}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={securitySettings.requirePhoneVerification}
                    onChange={(e) => setSecuritySettings(prev => ({
                      ...prev,
                      requirePhoneVerification: e.target.checked
                    }))}
                  />
                }
                label="Require Phone Verification"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={securitySettings.enableBrowserFingerprinting}
                    onChange={(e) => setSecuritySettings(prev => ({
                      ...prev,
                      enableBrowserFingerprinting: e.target.checked
                    }))}
                  />
                }
                label="Enable Browser Fingerprinting"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setSettingsOpen(false)}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TripSharingManagementPage;